import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
} from '@mui/material';
import type { Progetto, ProgettoInput } from '../types';

interface ProjectFormProps {
  open: boolean;
  progetto?: Progetto | null;
  onClose: () => void;
  onSave: (progetto: ProgettoInput) => Promise<void>;
}

const ProjectForm: React.FC<ProjectFormProps> = ({ open, progetto, onClose, onSave }) => {
  const [formData, setFormData] = useState<ProgettoInput>({
    titolo: '',
    descrizione: '',
    id_hrp: undefined,
    erpnext_key: '',
    budget_gg_uu: undefined,
    budget_residuo_gg_uu: undefined,
    data_inizio: undefined,
    data_fine: undefined,
    commerciale: '',
    pm: '',
    cliente: '',
    cliente_finale: '',
    enabled: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Popola form quando si apre in modalità edit
  useEffect(() => {
    if (open && progetto) {
      setFormData({
        titolo: progetto.titolo,
        descrizione: progetto.descrizione || '',
        id_hrp: progetto.id_hrp,
        erpnext_key: progetto.erpnext_key || '',
        budget_gg_uu: progetto.budget_gg_uu,
        budget_residuo_gg_uu: progetto.budget_residuo_gg_uu,
        data_inizio: progetto.data_inizio ? progetto.data_inizio.split('T')[0] : undefined,
        data_fine: progetto.data_fine ? progetto.data_fine.split('T')[0] : undefined,
        commerciale: progetto.commerciale || '',
        pm: progetto.pm || '',
        cliente: progetto.cliente || '',
        cliente_finale: progetto.cliente_finale || '',
        enabled: progetto.enabled !== undefined ? progetto.enabled : true,
      });
    } else if (open && !progetto) {
      // Reset form per create
      setFormData({
        titolo: '',
        descrizione: '',
        id_hrp: undefined,
        erpnext_key: '',
        budget_gg_uu: undefined,
        budget_residuo_gg_uu: undefined,
        data_inizio: undefined,
        data_fine: undefined,
        commerciale: '',
        pm: '',
        cliente: '',
        cliente_finale: '',
        enabled: true,
      });
    }
    setErrors({});
  }, [open, progetto]);

  const handleChange = (field: keyof ProgettoInput) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value === '' ? undefined : value,
    }));
    // Rimuovi errore quando l'utente modifica il campo
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleNumberChange = (field: keyof ProgettoInput) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value === '' ? undefined : parseInt(value),
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSwitchChange = (field: keyof ProgettoInput) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.checked,
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.titolo || formData.titolo.trim() === '') {
      newErrors.titolo = 'Il titolo è obbligatorio';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error: any) {
      setErrors({ submit: error.message || 'Errore nel salvataggio' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{progetto ? 'Modifica Progetto' : 'Nuovo Progetto'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Titolo"
              value={formData.titolo}
              onChange={handleChange('titolo')}
              error={!!errors.titolo}
              helperText={errors.titolo}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Descrizione"
              value={formData.descrizione}
              onChange={handleChange('descrizione')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="ID HRP"
              value={formData.id_hrp || ''}
              onChange={handleNumberChange('id_hrp')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Chiave ERPNext"
              value={formData.erpnext_key}
              onChange={handleChange('erpnext_key')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Budget (giorni/uomo)"
              value={formData.budget_gg_uu || ''}
              onChange={handleNumberChange('budget_gg_uu')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Budget Residuo (giorni/uomo)"
              value={formData.budget_residuo_gg_uu || ''}
              onChange={handleNumberChange('budget_residuo_gg_uu')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Data Inizio"
              value={formData.data_inizio || ''}
              onChange={handleChange('data_inizio')}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Data Fine"
              value={formData.data_fine || ''}
              onChange={handleChange('data_fine')}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="PM"
              value={formData.pm}
              onChange={handleChange('pm')}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Commerciale"
              value={formData.commerciale}
              onChange={handleChange('commerciale')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Cliente"
              value={formData.cliente}
              onChange={handleChange('cliente')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Cliente Finale"
              value={formData.cliente_finale}
              onChange={handleChange('cliente_finale')}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.enabled || false}
                  onChange={handleSwitchChange('enabled')}
                  color="primary"
                />
              }
              label="Progetto Abilitato"
            />
          </Grid>
        </Grid>

        {errors.submit && (
          <div style={{ color: 'red', marginTop: '16px' }}>{errors.submit}</div>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Annulla
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          {saving ? 'Salvataggio...' : 'Salva'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectForm;
